import { Queue } from '@rossum/api-client/queues';
import { Check, DoDisturb } from '@rossum/ui/icons';
import { Box, Button, Stack, Switch } from '@rossum/ui/material';
import { get, includes, map } from 'lodash';
import UsersIcon from 'mdi-react/AccountMultipleOutlineIcon';
import { useCallback, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { connect } from 'react-redux';
import { RouteChildrenProps } from 'react-router';
import { isString, isTruthy, pickBy } from 'remeda';
import { useUnpaginatedUsers } from '../../../../business/users/useUnpaginatedUsers';
import { PageLayoutV2 } from '../../../../components/PageLayoutV2/PageLayoutV2';
import InfoPlaceholder from '../../../../components/UI/InfoPlaceHolder';
import { pageSizes } from '../../../../constants/values';
import { QueueSettingsBreadcrumbs } from '../../../../features/queue-settings/components/QueueSettingsBreadcrumbs';
import { QueueSettingsTabs } from '../../../../features/queue-settings/components/QueueSettingsTabs';
import { usePatchQueue } from '../../../../features/queues/hooks/usePatchQueue';
import { parse, stringify } from '../../../../lib/url';
import {
  getRoleName,
  isUserAdminLike,
} from '../../../../redux/modules/groups/helpers';
import { getName } from '../../../../redux/modules/user/helpers';
import { Group } from '../../../../types/group';
import { State } from '../../../../types/state';
import { User, UserList } from '../../../../types/user';
import { Header } from '../../../../ui/header/Header';
import SearchInput from '../../../../ui/search-input/SearchInput';
import { BasicTile } from '../../../../ui/tiles/BasicTile';
import UsersFooter from '../../../Users/components/UsersFooter';
import styles from '../../styles.module.sass';
import { ToggleAllDialog, ToggleAllDialogAction } from './ToggleAllDialog';

type OwnProps = RouteChildrenProps & {
  selectedQueue: Queue;
};

type StateProps = {
  users: UserList;
  batchButtonsDisabled: boolean;
  count: number;
  groups: Array<Group>;
};

type Props = StateProps & OwnProps;

const defaultQuery = { pageSize: 15, page: 1, ordering: 'username' };

const QueueAccess = ({
  batchButtonsDisabled,
  users,
  selectedQueue,
  history,
  location,
  count,
  groups,
  match,
}: Props) => {
  const [dialogAction, setDialogAction] = useState<ToggleAllDialogAction>();
  const { search = '', ...query } = parse(location.search);
  const searchValue = isString(search) ? search : '';

  const parentPath = match?.url ?? '';

  const intl = useIntl();

  const { data: filteredUsers } = useUnpaginatedUsers({
    deleted: false,
    search: searchValue,
  });

  const { mutateAsync: patchQueue } = usePatchQueue();

  useEffect(() => {
    const { pageSize, page, ordering } = parse(location.search) || {};
    if (!ordering || !page || !pageSize || !includes(pageSizes, pageSize)) {
      history.replace({
        state: get(location, 'state'),
        search: stringify(defaultQuery),
      });
    }
  }, [location]); // eslint-disable-line react-hooks/exhaustive-deps

  const setSearchPhrase = (value: string) =>
    history.replace({
      state: get(location, 'state'),
      search: stringify(pickBy({ ...query, search: value, page: 1 }, isTruthy)),
    });

  const isEnabled = ({ url }: User) =>
    includes(get(selectedQueue, 'users', []), url);

  const isAdmin = ({ groups: userGroups }: User) =>
    isUserAdminLike(getRoleName(userGroups, groups));

  const onToggle = (enabled: boolean, user: User) => {
    const resolvedPayload = {
      users: enabled
        ? [...selectedQueue.users, user.url]
        : selectedQueue.users.filter(userUrl => userUrl !== user.url),
    };
    patchQueue({
      queueId: selectedQueue.id,
      payload: resolvedPayload,
    });
  };

  // TODO: This should probably not be copy-pasted around
  const onBackButtonClicked = useCallback(() => {
    // If the queue settings page was opened in a new tab, there is no history stack to go back, so fallbacking to "/documents" path instead
    if (history.length > 1) {
      return history.goBack();
    }
    return history.push('/documents');
  }, [history]);

  const onToggleAllSubmit = () => {
    if (!dialogAction || !filteredUsers) {
      setDialogAction(undefined);
      return;
    }

    // TODO: fetch all users only on dialog open not on the component render
    const filteredUsersUrls = filteredUsers.map(u => u.url);

    const usersToEnable = filteredUsersUrls;
    const usersToDisable = searchValue.length
      ? selectedQueue.users.filter(
          userUrl => !filteredUsersUrls.includes(userUrl)
        )
      : [];

    patchQueue({
      queueId: selectedQueue.id,
      payload: {
        users:
          dialogAction === 'disableUsersOnQueue'
            ? usersToDisable
            : usersToEnable,
      },
    });

    setDialogAction(undefined);
  };

  return (
    <PageLayoutV2
      renderHeader={params => (
        <Header
          scrollableDivRef={params.scrollableDivRef}
          breadcrumbs={
            <QueueSettingsBreadcrumbs
              queueName={selectedQueue?.name ?? ''}
              settingsPath={parentPath}
              breadcrumbs={[
                {
                  label: intl.formatMessage({
                    id: 'containers.settings.queues.queue.access',
                  }),
                  to: `${parentPath}/access`,
                },
              ]}
            />
          }
          tabs={<QueueSettingsTabs parentPath={parentPath} value="access" />}
          title={intl.formatMessage({
            id: 'containers.settings.queues.queue.access',
          })}
          description={intl.formatMessage({
            id: 'containers.settings.queues.queue.access.description',
          })}
          onBackButtonClicked={onBackButtonClicked}
        />
      )}
    >
      <Box
        className={styles.AccessLayout}
        sx={{ p: 4 }}
        data-page-title="queue-access"
      >
        <div className={styles.AccessHeader}>
          <div className={styles.AccessTitle}>
            <FormattedMessage id="containers.settings.queues.queue.title" />
          </div>
          {!batchButtonsDisabled && (
            <div className={styles.AccessBadges}>
              <Button
                color="secondary"
                size="small"
                startIcon={<Check />}
                data-cy="queue-access-enable-all"
                onClick={() => setDialogAction('enableUsersOnQueue')}
              >
                <FormattedMessage
                  id="containers.users.components.workspace.enableAll"
                  values={{ count }}
                />
              </Button>
              <Button
                color="secondary"
                size="small"
                data-cy="queue-access-disable-all"
                onClick={() => setDialogAction('disableUsersOnQueue')}
                startIcon={<DoDisturb />}
              >
                <FormattedMessage
                  id="containers.users.components.workspace.disableAll"
                  values={{ count }}
                />
              </Button>
            </div>
          )}
          <SearchInput
            value={searchValue}
            onChange={setSearchPhrase}
            placeholder={intl.formatMessage({
              id: 'containers.settings.queues.queue.searchPlaceholder',
            })}
          />
        </div>
        {users.length ? (
          <Stack direction="column" spacing={1} pt={2}>
            {map(users, user => (
              <BasicTile key={user.id}>
                <Stack direction="row" gap={4}>
                  <Switch
                    data-cy="queue-access-enable-user-toggle"
                    disabled={!selectedQueue || isAdmin(user)}
                    onChange={(_, value) => onToggle(value, user)}
                    checked={isEnabled(user) || isAdmin(user)}
                    size="small"
                  />
                  {getName(user)}
                </Stack>

                {isAdmin(user) && (
                  <FormattedMessage id="containers.settings.queues.queue.admin" />
                )}
              </BasicTile>
            ))}
          </Stack>
        ) : (
          <InfoPlaceholder
            icon={<UsersIcon />}
            title="containers.settings.queues.noUserFound"
            text="containers.settings.queues.noUserFound.text"
          />
        )}
      </Box>
      <ToggleAllDialog
        onSubmit={onToggleAllSubmit}
        onClose={() => setDialogAction(undefined)}
        action={dialogAction}
        queueName={selectedQueue.name}
        count={count}
      />
      <UsersFooter />
    </PageLayoutV2>
  );
};

const mapStateToProps = ({ groups, users }: State, { location }: OwnProps) => {
  // TODO: take users from RQ and connect it to pagination instead of redux
  const isSearchActive = get(parse(location.search), 'search');
  const usersTotal = get(users, 'pagination.total', 0);
  const adminsTotal = get(users, 'adminsCount', 0);

  return {
    users: users.list,
    batchButtonsDisabled: usersTotal === adminsTotal,
    count: isSearchActive ? Math.max(usersTotal - adminsTotal, 0) : 0,
    groups,
  };
};

export default connect(mapStateToProps)(QueueAccess);
